import { Box, styled } from "@mui/material";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useIsPhone } from "contexts/panelMode";
import { coordinatesActions } from "features/coordinates/coordinatesSlice";
import { measureActions, selectMeasure } from "features/measure";
import {
    mobileMeasureActions,
    selectCoordinateSessionIds,
    selectDimensionSessionStart,
    selectLaserSessionIds,
    selectPointLineSessionIds,
} from "features/mobileMeasure/mobileMeasureSlice";
import { clippingOutlineLaserActions } from "features/outlineLaser/outlineLaserSlice";
import { pointLineActions } from "features/pointLine";
import { Picker, renderActions, selectPicker } from "features/render";

import { PROCORE_TOOL_IDS, TOOLBAR_LAYOUT, TOOLBAR_Z_INDEX } from "../constants";
import { BottomToolbarExtension } from "./BottomToolbarExtension";
import { BottomToolbarGeneralContent } from "./BottomToolbarGeneralContent";
import { BottomToolbarTabs } from "./BottomToolbarTabs";
import { BottomToolbarToolControlsContent } from "./BottomToolbarToolControlsContent";
import { BottomToolbarToolsContent } from "./BottomToolbarToolsContent";
import { selectActiveTool } from "./slice";

const ToolbarContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isPhone",
})<{ isPhone?: boolean }>`
    position: absolute;
    bottom: max(
        ${({ isPhone }) => (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-bottom, 0px)
    );
    left: max(
        ${({ isPhone }) => (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-left, 0px)
    );
    right: max(
        ${({ isPhone }) => (isPhone ? TOOLBAR_LAYOUT.MARGIN_MOBILE : TOOLBAR_LAYOUT.MARGIN_TABLET)}px,
        env(safe-area-inset-right, 0px)
    );
    z-index: ${TOOLBAR_Z_INDEX.TOOLBAR};
    display: flex;
    align-items: flex-end;
    /* No gap - extension slides from under the tabs */
    pointer-events: none;
`;

/**
 * Mobile Bottom Toolbar
 *
 * Horizontal toolbar at the bottom of the screen with General/Tools tabs
 * and expandable extension area.
 *
 * Custom implementation for complex tab/extension UI requirements.
 */
export function BottomToolbar() {
    const dispatch = useAppDispatch();
    const activeTool = useAppSelector(selectActiveTool);
    const currentPicker = useAppSelector(selectPicker);
    const isPhone = useIsPhone();

    //TODO: Extract Pickers and tools to per-tool files
    // Measurement pickers that need to be stopped when exiting
    const measurementPickers = [
        Picker.Measurement,
        Picker.Area,
        Picker.PointLine,
        Picker.Coordinates,
        Picker.Dimension,
        Picker.OutlineLaser,
        Picker.Manhole,
    ];

    const measure = useAppSelector(selectMeasure);
    const dimensionSessionStart = useAppSelector(selectDimensionSessionStart);
    const laserSessionIds = useAppSelector(selectLaserSessionIds);
    const coordinateSessionIds = useAppSelector(selectCoordinateSessionIds);
    const pointLineSessionIds = useAppSelector(selectPointLineSessionIds);

    const handleSave = () => {
        if (
            currentPicker === Picker.Dimension ||
            currentPicker === Picker.OutlineLaser ||
            currentPicker === Picker.Coordinates ||
            currentPicker === Picker.PointLine
        ) {
            dispatch(mobileMeasureActions.reset());
            dispatch(renderActions.stopPicker(currentPicker));
        }
    };

    const handleExit = () => {
        if (activeTool === PROCORE_TOOL_IDS.MEASURE && measurementPickers.includes(currentPicker)) {
            if (currentPicker === Picker.Dimension) {
                const total = measure.selectedEntities.length;
                for (let i = total - 1; i >= dimensionSessionStart; i--) {
                    dispatch(measureActions.deleteMeasureSet(i));
                }
            } else if (currentPicker === Picker.OutlineLaser) {
                for (const id of laserSessionIds) {
                    dispatch(clippingOutlineLaserActions.removeLaser(id));
                }
            } else if (currentPicker === Picker.PointLine) {
                for (const id of pointLineSessionIds) {
                    dispatch(pointLineActions.deletePointlineById(id));
                }
            } else if (currentPicker === Picker.Coordinates) {
                for (const id of coordinateSessionIds) {
                    dispatch(coordinatesActions.removeCoordinate(id));
                }
            }
            dispatch(mobileMeasureActions.reset());
            dispatch(renderActions.stopPicker(currentPicker));
        }
        if (activeTool === PROCORE_TOOL_IDS.SECTION && currentPicker === Picker.SectionPlane) {
            dispatch(renderActions.stopPicker(Picker.SectionPlane));
        }
        //TODO: Implement a common logic for tool exit invocation
    };

    return (
        <ToolbarContainer isPhone={isPhone}>
            {/* Main tabs (General/Tools or Save/Exit) */}
            <BottomToolbarTabs onSave={handleSave} onExit={handleExit} />

            {/* Expandable extension with buttons */}
            <BottomToolbarExtension
                generalContent={<BottomToolbarGeneralContent />}
                toolsContent={<BottomToolbarToolsContent />}
                toolControlsContent={activeTool ? <BottomToolbarToolControlsContent toolId={activeTool} /> : null}
            />
        </ToolbarContainer>
    );
}
